/**
 * Purge des données opérationnelles d'une fiche entreprise (admin, Mode Debug actif).
 *
 * Avant tout, l'aperçu : ce qui sera supprimé (compteurs par collection) et ce
 * qui sera conservé (référentiel, oppositions). Le mot de passe administrateur
 * est redemandé ; le serveur refuse hors Mode Debug, hors organisation, ou
 * sans confirmation — la modale ne fait qu'exposer ces règles.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";
import { Loader } from "@/crm/ui";

export default function PurgeCompanyModal({ company, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/companies/${company.id}/purge-preview`)
      .then(({ data }) => { if (alive) setPreview(data); })
      .catch((e) => { if (alive) setError(apiError(e.response?.data?.detail)); });
    return () => { alive = false; };
  }, [company.id]);

  const debugActive = Boolean(preview?.debug_active);
  const ready = debugActive && password.length > 0 && !loading;

  const purge = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/purge`, { password, confirm: true });
      toast.success(`Fiche purgée : ${data.total} document(s) supprimé(s)`);
      onDone?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  const toDelete = (preview?.will_delete || []).filter((i) => i.count > 0);

  return (
    <Modal onClose={onClose} testid="purge-company-modal" label="Purger la fiche entreprise" className="max-w-[520px]">
      <div className="flex items-center gap-3 mb-3 text-rose-600">
        <div className="h-11 w-11 rounded-full bg-rose-50 flex items-center justify-center"><AlertTriangle size={22} /></div>
        <div>
          <h3 className="font-heading font-bold text-lg text-slate-900">Purge entreprise</h3>
          <p className="text-xs text-slate-500">{company.nom}{company.siret ? ` · ${company.siret}` : ""}</p>
        </div>
      </div>

      {error && <p className="text-sm text-rose-600 mb-3" data-testid="purge-preview-error">{error}</p>}
      {!preview && !error && <Loader label="Analyse de la fiche…" />}

      {preview && (
        <>
          {!debugActive && (
            <div data-testid="purge-needs-debug" className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Le Mode Debug doit être actif pour purger une fiche. Activez-le depuis <b>Administration › Paramètres › Mode Debug</b>.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-600 mb-1.5 flex items-center gap-1"><Trash2 size={12} /> Sera supprimé</div>
              {toDelete.length === 0 ? <p className="text-xs text-slate-500">Aucune donnée opérationnelle.</p> : (
                <ul className="text-xs text-slate-700 space-y-0.5" data-testid="purge-will-delete">
                  {toDelete.map((i) => <li key={i.collection}>• <span className="font-mono">{i.count}</span> {i.label}</li>)}
                </ul>
              )}
              {preview.linked_companies_to_unlink > 0 && (
                <p className="text-xs text-slate-500 mt-1.5">+ {preview.linked_companies_to_unlink} lien(s) « sociétés liées » retiré(s)</p>
              )}
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 mb-1.5 flex items-center gap-1"><ShieldCheck size={12} /> Sera conservé</div>
              <ul className="text-xs text-slate-700 space-y-0.5">
                <li>• Nom, SIREN / SIRET</li>
                <li>• Adresse, téléphone, email, site</li>
                <li>• Informations référentielles (NAF, dirigeants, effectif…)</li>
                <li>• Oppositions « Ne plus contacter » ({preview.will_keep?.exclusions ?? 0})</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            La fiche est remise à l'état « Nouveau » (statut, attribution, tags, notes, contenus IA). <b>Irréversible.</b>
            L'action est enregistrée dans les activités système.
          </p>
          <label className="text-xs text-slate-500">Mot de passe Mode Debug</label>
          <input data-testid="purge-password-input" type="password" value={password} autoFocus autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" disabled={!debugActive}
            onKeyDown={(e) => { if (e.key === "Enter") purge(); }}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-slate-50" />
        </>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button data-testid="purge-cancel-btn" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Annuler</button>
        <button data-testid="purge-company-confirm-btn" onClick={purge} disabled={!ready}
          className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {loading ? "Purge…" : "Purger définitivement"}
        </button>
      </div>
    </Modal>
  );
}
